/**
 * Settings Menu Component
 * Unified settings menu that includes:
 * - Sound settings (music and sound effects)
 * - Window mode (fullscreen vs windowed with title bar for Electron)
 * - Logout button
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from '../hooks/useSoundEffects';
import { useBrowserFullscreen } from '../hooks/useBrowserFullscreen';
import { isElectronClient } from '../utils/isElectronClient';
import { SoundSettingsControls } from '../music/SoundSettingsControls';
import './SettingsMenu.css';

function SettingsMenu({ onLogout, onResign = null }) {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [displayMode, setDisplayModeState] = useState('fullscreen'); // 'fullscreen' | 'windowed'
  const [isElectron, setIsElectron] = useState(() => isElectronClient());
  const {
    supported: browserFullscreenSupported,
    isActive: browserFullscreenActive,
    toggle: toggleBrowserFullscreen,
  } = useBrowserFullscreen();
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) {
      throw new Error('SettingsMenu: buttonRef is required to position the menu');
    }
    const rect = buttonRef.current.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 8,
      left: rect.left,
    });
  }, []);

  // Electron: sync display mode + native fullscreen state via preload bridge.
  useEffect(() => {
    const electron = window.electron;
    if (!electron || !electron.window) {
      return undefined;
    }

    electron.window.getDisplayMode().then((mode) => {
      setDisplayModeState(mode === 'windowed' ? 'windowed' : 'fullscreen');
    });

    electron.window.isFullscreen().then((fullscreen) => {
      setIsFullscreen(fullscreen);
    });

    electron.window.onFullscreenChange((fullscreen) => {
      setIsFullscreen(fullscreen);
    });

    return () => {
      if (electron && electron.window) {
        electron.window.removeFullscreenListener();
      }
    };
  }, []);

  // Portal menu to body so it stacks above BattleUI overlays (e.g. battle action log z-index 99990).
  useEffect(() => {
    if (!isMenuOpen) return undefined;

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isMenuOpen, updateMenuPosition]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef &&
        !buttonRef.current?.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleClick = () => {
    playSoundEffect('/sounds/click.mp3');
    setIsMenuOpen(prev => !prev);
  };

  const handleClose = () => {
    setIsMenuOpen(false);
  };

  const handleFullscreenToggle = () => {
    playSoundEffect('/sounds/click.mp3');
    const electron = window.electron;
    if (electron && electron.window) {
      electron.window.toggleFullscreen();
      return;
    }
    toggleBrowserFullscreen().catch((err) => {
      console.error('Browser fullscreen toggle failed:', err);
    });
  };

  const handleSetDisplayMode = (mode) => {
    if (mode === displayMode) return;
    const electron = window.electron;
    if (!electron || !electron.window || !electron.window.setDisplayMode) return;
    playSoundEffect('/sounds/click.mp3');
    electron.window.setDisplayMode(mode);
    setDisplayModeState(mode);
    // Window will recreate and reload; menu will unmount
  };

  const handleExit = () => {
    playSoundEffect('/sounds/click.mp3');
    handleClose();
    const electron = window.electron;
    if (!electron || typeof electron.quitApp !== 'function') {
      throw new Error(
        'SettingsMenu: window.electron.quitApp is required to exit the Electron client'
      );
    }
    electron.quitApp();
  };

  const handleLogout = () => {
    playSoundEffect('/sounds/click.mp3');
    handleClose();
    if (onLogout) {
      onLogout();
    }
  };

  const handleResign = () => {
    if (typeof onResign !== 'function') {
      throw new Error('SettingsMenu: onResign is required when resign button is shown');
    }
    playSoundEffect('/sounds/click.mp3');
    handleClose();
    onResign();
  };

  const menuPanel = isMenuOpen ? (
    <div
      className="settings-menu settings-menu--portaled"
      ref={menuRef}
      style={{ top: menuPosition.top, left: menuPosition.left }}
    >
      <button 
        className="settings-menu-close"
        onClick={handleClose}
        aria-label={t('common.close', 'Close')}
      >
        ×
      </button>

      <div className="settings-menu-content">
        {/* Sound Settings Section */}
        <div className="settings-menu-section">
          <div className="settings-menu-section-title">
            {t('settings.sound', 'Sound')}
          </div>
          <SoundSettingsControls variant="settings" />
        </div>

        {/* Window Mode Section (Electron only) */}
        {isElectron && (
          <div className="settings-menu-section">
            <div className="settings-menu-section-title">
              {t('settings.windowMode', 'Window Mode')}
            </div>
            <div className="settings-menu-display-mode-row">
              <button
                className={`settings-menu-button settings-menu-mode-btn ${displayMode === 'fullscreen' ? 'settings-menu-mode-active' : ''}`}
                onClick={() => handleSetDisplayMode('fullscreen')}
                title={t('settings.fullscreenModeDesc', 'Full screen, no title bar')}
              >
                {t('settings.fullscreenMode', 'Fullscreen')}
              </button>
              <button
                className={`settings-menu-button settings-menu-mode-btn ${displayMode === 'windowed' ? 'settings-menu-mode-active' : ''}`}
                onClick={() => handleSetDisplayMode('windowed')}
                title={t('settings.windowedModeDesc', 'Window with title bar – drag to move')}
              >
                {t('settings.windowedMode', 'Windowed')}
              </button>
            </div>
            {displayMode === 'windowed' && (
              <button
                className="settings-menu-button"
                onClick={handleFullscreenToggle}
              >
                {isFullscreen
                  ? t('settings.exitFullscreen', 'Exit Fullscreen (F11)')
                  : t('settings.enterFullscreen', 'Enter Fullscreen (F11)')
                }
              </button>
            )}
            <button
              className="settings-menu-button settings-menu-button-exit"
              onClick={handleExit}
            >
              {t('settings.exit', 'Exit')}
            </button>
          </div>
        )}

        {/* Browser fullscreen (web desktop — Fullscreen API) */}
        {!isElectron && browserFullscreenSupported && (
          <div className="settings-menu-section">
            <div className="settings-menu-section-title">
              {t('settings.display', 'Display')}
            </div>
            <button
              type="button"
              className="settings-menu-button"
              onClick={handleFullscreenToggle}
            >
              {browserFullscreenActive
                ? t('settings.exitFullscreen', 'Exit Fullscreen (F11)')
                : t('settings.enterFullscreen', 'Enter Fullscreen (F11)')}
            </button>
            <p className="settings-menu-hint">
              {t('fullscreen.settingsHint')}
            </p>
          </div>
        )}

        {/* Logout Section */}
        <div className="settings-menu-section">
          {typeof onResign === 'function' ? (
            <button
              type="button"
              className="settings-menu-button settings-menu-button-resign"
              onClick={handleResign}
            >
              {t('battleUI.resign', 'Resign')}
            </button>
          ) : null}
          <button
            type="button"
            className="settings-menu-button settings-menu-button-logout"
            onClick={handleLogout}
          >
            {t('common.logout', 'Logout')}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div 
      ref={buttonRef}
      className="settings-menu-container"
      style={{ position: 'relative', marginRight: '10px' }}
    >
      <div
        className="settings-menu-trigger"
        onClick={handleClick}
        title={t('settings.open', 'Open settings')}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
      >
        <svg 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
        >
          <path 
            d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94L14.4 2.81c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" 
            fill="white"
          />
        </svg>
      </div>

      {menuPanel && typeof document !== 'undefined'
        ? createPortal(menuPanel, document.body)
        : null}
    </div>
  );
}

export default SettingsMenu;
